
import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../_services/auth.service';
import { TokenService } from '../_services/token.service';
import { UPLOAD_LINK_ENDPOINTS } from './constants/upload-link.constants';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  constructor(
    private tokenService: TokenService,
    private authService: AuthService,
    private toastrService: ToastrService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<object>> {
    let authReq = request;
    const token = this.tokenService.getToken();
    if (
      token !== null &&
      !request.url.includes('webappunregistered/') &&
      !request.url.includes('https://ingfiles.blob.core.windows.net')
    ) {
      authReq = this.addTokenHeader(request, token);
    }

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (
          error instanceof HttpErrorResponse &&
          !authReq.url.includes('login') &&
          error.status === 401 &&
          !request.url.includes(environment.apiUrl + 'refreshtoken') &&
          !this.isUploadLinkRequest(request.url)
        ) {
          const isExternalService =
            request.url.includes('GISService') ||
            request.url.includes('pipeinspection') ||
            request.url.includes('manhole-inspection') ||
            request.url.includes('pipeadoption') ||
            request.url.includes('engineerclasses') ||
            request.url.includes('mappenterprise') ||
            request.url.includes('isybauxmlfixer') ||
            request.url.includes('IsybauManagement');

          if (isExternalService) {
            return this.handle401ErrorSilent(authReq, next);
          }
          return this.handle401Error(authReq, next);
        }
        return throwError(error);
      })
    );
  }

  private isUploadLinkRequest(url: string): boolean {
    if (!url.startsWith(environment.apiUrl)) {
      return false;
    }
    const endpoint = url.slice(environment.apiUrl.length).split(/[/?]/)[0];
    return UPLOAD_LINK_ENDPOINTS.includes(endpoint);
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    const tenantId = this.tokenService.GetTenantId();
    if (tenantId && request.url.startsWith(environment.apiUrl)) {
      headers['X-Tenant-Id'] = tenantId;
    }
    return request.clone({ setHeaders: headers });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const refreshToken = this.tokenService.getRefreshToken();
      const accessToken = this.tokenService.getToken();
      if (refreshToken && accessToken)
        return this.authService.RefreshToken(accessToken, refreshToken).pipe(
          switchMap((token: any) => {
            const decoded: any = jwt_decode(this.tokenService.getToken()!);
            const decodedNew: any = jwt_decode(token.accessToken);
            if (decoded[environment.roleKey].toString() !== decodedNew[environment.roleKey].toString()) {
              window.location.reload();
            }
            this.isRefreshing = false;
            this.tokenService.saveToken(token.accessToken);
            this.tokenService.saveRefreshToken(token.refreshToken);
            this.refreshTokenSubject.next(token.accessToken);
            return next.handle(this.addTokenHeader(request, token.accessToken));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.tokenService.signOut();
            return throwError(err);
          })
        );
    }
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private handle401ErrorSilent(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const refreshToken = this.tokenService.getRefreshToken();
      const accessToken = this.tokenService.getToken();
      if (refreshToken && accessToken)
        return this.authService.RefreshToken(accessToken, refreshToken).pipe(
          switchMap((token: any) => {
            const decoded: any = jwt_decode(this.tokenService.getToken()!);
            const decodedNew: any = jwt_decode(token.accessToken);
            if (decoded[environment.roleKey].toString() !== decodedNew[environment.roleKey].toString()) {
              window.location.reload();
            }
            this.isRefreshing = false;
            this.tokenService.saveToken(token.accessToken);
            this.tokenService.saveRefreshToken(token.refreshToken);
            this.refreshTokenSubject.next(token.accessToken);
            return next.handle(this.addTokenHeader(request, token.accessToken));
          }),
          catchError(() => {
            this.isRefreshing = false;
            return throwError(() => new Error('Token refresh failed'));
          })
        );
    }
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }
}

export const authInterceptorProviders = [{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }];
